import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminCreateInsurancePolicy,
  AdminCreateInsurancePolicyProduct,
  AdminDeleteInsurancePolicy,
  AdminDeleteInsurancePolicyProduct,
  AdminFetchInsurancePolicies,
  AdminUpdateInsurancePolicy,
  AdminUpdateInsurancePolicyProduct,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import { InsurancePolicyOperationType } from 'src/constants/insurance-policy/enums'
import type {
  CreateInsurancePolicyPayload,
  CreateInsurancePolicyProductPayload,
  DeleteInsurancePolicyPayload,
  DeleteInsurancePolicyProductPayload,
  FetchInsurancePoliciesPayload,
  InsurancePolicy,
  InsurancePolicyProduct,
  UpdateInsurancePolicyPayload,
  UpdateInsurancePolicyProductPayload,
} from 'src/interfaces/insurancePolicy'
import { normalizeApiListPayload } from 'src/utils/normalizeApiListPayload'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

/**
 * The optional body fields shared by the create and the update. Deliberately NOT including
 * `insuranceProductId`, `feeFrequency` or `approvalType`: those three are create-only, and the
 * update pipeline runs `forbidNonWhitelisted`, so sending one on a PUT 400s the request.
 *
 * Six of these — `description`, `insuranceRequirements`, `coverage`, `coverageDetails`,
 * `coverageDetailsPdfUrl` and `documentUploadTitleWhenOptedIn` — are typed `string | null`
 * rather than plain `string` because the UPDATE mapper sends an explicit `null` to clear a
 * field; CREATE only ever sends a string or omits the key. `copyOptionalPolicyProductFields`
 * below tests `!== undefined`, so a `null` is forwarded into the body unchanged.
 */
interface OptionalPolicyProductFields {
  feeCents?: number
  feePercent?: number
  feeCapCents?: number
  insuranceDocusignTemplateId?: string
  description?: string | null
  insuranceRequirements?: string | null
  coverage?: string | null
  coverageDetails?: string | null
  coverageDetailsPdfUrl?: string | null
  manualUploadRequirements?: string[]
  requireDocumentUploadWhenOptedIn?: boolean
  documentUploadTitleWhenOptedIn?: string | null
  requireExpirationDate?: boolean
}

const OPTIONAL_POLICY_PRODUCT_FIELDS: Array<keyof OptionalPolicyProductFields> = [
  'feeCents',
  'feePercent',
  'feeCapCents',
  'insuranceDocusignTemplateId',
  'description',
  'insuranceRequirements',
  'coverage',
  'coverageDetails',
  'coverageDetailsPdfUrl',
  'manualUploadRequirements',
  'requireDocumentUploadWhenOptedIn',
  'documentUploadTitleWhenOptedIn',
  'requireExpirationDate',
]

/**
 * Copies the optional fields that are actually set.
 *
 * The test is `!== undefined`, NOT truthiness — unlike `buildInsuranceProductBody`, which only
 * ever carries strings. Here `allowOptOut: false`, `feeCents: 0`, `feeCapCents: 0` and
 * `requireDocumentUploadWhenOptedIn: false` are all meaningful values a truthiness check would
 * silently drop.
 *
 * An unset optional is OMITTED rather than sent as `''` or `null`: `coverageDetailsPdfUrl` is
 * `@IsUrl`, so an empty string is a hard 400. The form's mapper has already turned every blank
 * string into `undefined`.
 */
const copyOptionalPolicyProductFields = (
  source: OptionalPolicyProductFields,
): OptionalPolicyProductFields => {
  // Built as a `Record<string, unknown>` rather than `OptionalPolicyProductFields` directly:
  // the array is typed field-by-field above, so this assignment is safe, but TypeScript cannot
  // correlate the key and the value type across a loop.
  const body: Record<string, unknown> = {}

  OPTIONAL_POLICY_PRODUCT_FIELDS.forEach((field) => {
    const value = source[field]

    if (value !== undefined) {
      body[field] = value
    }
  })

  return body as OptionalPolicyProductFields
}

/**
 * `InsurancePolicyProductEntity.feePercent` is `decimal(4,2)` — node-postgres hands decimal
 * columns back as strings, and the response DTO exposes this one with a bare `@Expose()` and no
 * `@Type(() => Number)`, so the wire value is `"2.50"`, not `2.5` (ENG-750 tracks the backend
 * fix). Unlike `utils/paymentConfig.ts`'s `toNumber`, `null`/`undefined` must SURVIVE as `null`
 * here rather than fold to `0` — the grid renders a missing percent fee as an em dash, and `0`
 * would falsely claim a 0% fee was recorded.
 */
const toNullableNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return null
    }

    return value
  }

  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()

  if (trimmed === '') {
    return null
  }

  const parsed = Number(trimmed)

  if (!Number.isFinite(parsed)) {
    return null
  }

  return parsed
}

export function* onFetchInsurancePolicies(action: AsyncAction<FetchInsurancePoliciesPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminFetchInsurancePolicies.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminInsurancePolicies,
      {
        method: 'GET',
        query: { partnerId },
      },
    )

    // A policy with no products comes back without the key at all, so normalizing here is what
    // lets `InsurancePolicy.insurancePoliciesProducts` be non-optional for every consumer.
    // `feePercent` is coerced through `toNullableNumber` in the same pass, which is what makes
    // the declared `number | null` type true for everything downstream — see ENG-750.
    const policies = normalizeApiListPayload<InsurancePolicy>(payload).map((policy) => {
      return {
        ...policy,
        insurancePoliciesProducts: (policy.insurancePoliciesProducts ?? []).map((policyProduct) => {
          return {
            ...policyProduct,
            feePercent: toNullableNumber(policyProduct.feePercent),
          }
        }),
      }
    })

    yield put(AdminFetchInsurancePolicies.success(policies))

    if (action.next) {
      action.next(null, policies)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.insurancePolicy.onFetchInsurancePolicies] error',
    })

    yield put(AdminFetchInsurancePolicies.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load insurance policies'))
    }
  }
}

export function* onCreateInsurancePolicy(action: AsyncAction<CreateInsurancePolicyPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminCreateInsurancePolicy.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    const { payload }: { payload: InsurancePolicy } = yield call(
      callSecureApi,
      Endpoints.AdminInsurancePolicies,
      {
        method: 'POST',
        // Built field by field: the pipeline runs `forbidNonWhitelisted`. The DTO also accepts
        // a nested `insurancePoliciesProducts`, deliberately never sent — products are added
        // one at a time so each carries its own `operationType`.
        body: { partnerId, name: action.payload.name },
      },
    )

    yield put(AdminCreateInsurancePolicy.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.insurancePolicy.onCreateInsurancePolicy] error',
    })

    yield put(AdminCreateInsurancePolicy.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to create insurance policy'))
    }
  }
}

export function* onUpdateInsurancePolicy(action: AsyncAction<UpdateInsurancePolicyPayload>) {
  const insurancePolicyPermanentId = action.payload?.insurancePolicyPermanentId

  if (!insurancePolicyPermanentId) {
    yield put(AdminUpdateInsurancePolicy.failed(new Error('Missing insurancePolicyPermanentId')))

    if (action.next) {
      action.next('Missing insurancePolicyPermanentId')
    }

    return
  }

  try {
    const { payload }: { payload: InsurancePolicy } = yield call(
      callSecureApi,
      Endpoints.AdminInsurancePolicy,
      {
        method: 'PUT',
        params: [{ field: ':insurancePolicyPermanentId', value: insurancePolicyPermanentId }],
        // `AdminInsurancePolicyUpdateDto` whitelists `name` and nothing else.
        body: { name: action.payload.name },
      },
    )

    yield put(AdminUpdateInsurancePolicy.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.insurancePolicy.onUpdateInsurancePolicy] error',
    })

    yield put(AdminUpdateInsurancePolicy.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to update insurance policy'))
    }
  }
}

export function* onDeleteInsurancePolicy(action: AsyncAction<DeleteInsurancePolicyPayload>) {
  const insurancePolicyPermanentId = action.payload?.insurancePolicyPermanentId

  if (!insurancePolicyPermanentId) {
    yield put(AdminDeleteInsurancePolicy.failed(new Error('Missing insurancePolicyPermanentId')))

    if (action.next) {
      action.next('Missing insurancePolicyPermanentId')
    }

    return
  }

  try {
    // No `forbiddenIsBusinessError` here: unlike the global insurance-PRODUCT delete, this
    // route answers 400 (a Nest ValidationError) for its domain rule, not 403.
    yield call(callSecureApi, Endpoints.AdminInsurancePolicy, {
      method: 'DELETE',
      params: [{ field: ':insurancePolicyPermanentId', value: insurancePolicyPermanentId }],
    })

    yield put(AdminDeleteInsurancePolicy.success(insurancePolicyPermanentId))

    if (action.next) {
      action.next(null, insurancePolicyPermanentId)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.insurancePolicy.onDeleteInsurancePolicy] error',
    })

    yield put(AdminDeleteInsurancePolicy.failed(error))

    if (action.next) {
      // A 400 here carries `['Insurance policy is in use']` — a string ARRAY, which
      // `resolveErrorMessage` joins so the reason reaches the admin instead of the fallback.
      action.next(resolveErrorMessage(error, 'Failed to delete insurance policy'))
    }
  }
}

export function* onCreateInsurancePolicyProduct(
  action: AsyncAction<CreateInsurancePolicyProductPayload>,
) {
  const insurancePolicyPermanentId = action.payload?.insurancePolicyPermanentId

  if (!insurancePolicyPermanentId) {
    yield put(
      AdminCreateInsurancePolicyProduct.failed(new Error('Missing insurancePolicyPermanentId')),
    )

    if (action.next) {
      action.next('Missing insurancePolicyPermanentId')
    }

    return
  }

  try {
    const { payload }: { payload: InsurancePolicyProduct } = yield call(
      callSecureApi,
      Endpoints.AdminInsurancePolicyProducts,
      {
        method: 'POST',
        params: [{ field: ':insurancePolicyPermanentId', value: insurancePolicyPermanentId }],
        // `operationType` is a REQUIRED query param (`@IsEnum`, no `@IsOptional`) and must
        // never appear in the body — `forbidNonWhitelisted` would reject the whole request.
        query: { operationType: action.payload.operationType },
        body: {
          insuranceProductId: action.payload.insuranceProductId,
          allowOptOut: action.payload.allowOptOut,
          feeFrequency: action.payload.feeFrequency,
          approvalType: action.payload.approvalType,
          ...copyOptionalPolicyProductFields(action.payload),
        },
      },
    )

    yield put(AdminCreateInsurancePolicyProduct.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.insurancePolicy.onCreateInsurancePolicyProduct] error',
    })

    yield put(AdminCreateInsurancePolicyProduct.failed(error))

    if (action.next) {
      // A 409 here carries 'Insurance product with ID … already assigned to that policy'.
      action.next(resolveErrorMessage(error, 'Failed to add the product to this policy'))
    }
  }
}

export function* onUpdateInsurancePolicyProduct(
  action: AsyncAction<UpdateInsurancePolicyProductPayload>,
) {
  const insurancePolicyPermanentId = action.payload?.insurancePolicyPermanentId
  const insuranceProductId = action.payload?.insuranceProductId

  if (!insurancePolicyPermanentId) {
    yield put(
      AdminUpdateInsurancePolicyProduct.failed(new Error('Missing insurancePolicyPermanentId')),
    )

    if (action.next) {
      action.next('Missing insurancePolicyPermanentId')
    }

    return
  }

  if (!insuranceProductId) {
    yield put(AdminUpdateInsurancePolicyProduct.failed(new Error('Missing insuranceProductId')))

    if (action.next) {
      action.next('Missing insuranceProductId')
    }

    return
  }

  try {
    const { payload }: { payload: InsurancePolicyProduct } = yield call(
      callSecureApi,
      Endpoints.AdminInsurancePolicyProduct,
      {
        method: 'PUT',
        params: [
          { field: ':insurancePolicyPermanentId', value: insurancePolicyPermanentId },
          { field: ':insuranceProductId', value: insuranceProductId },
        ],
        query: { operationType: action.payload.operationType },
        // `insuranceProductId` is the route param, and `feeFrequency` / `approvalType` are
        // create-only — none of the three is whitelisted on the update DTO, and `approvalType`
        // is `@ValidateIf(hasOwnProperty)`, so it must be ABSENT rather than `undefined`. Only
        // `allowOptOut` and the shared optionals are copied.
        body: {
          ...(action.payload.allowOptOut === undefined
            ? {}
            : { allowOptOut: action.payload.allowOptOut }),
          ...copyOptionalPolicyProductFields(action.payload),
        },
      },
    )

    yield put(AdminUpdateInsurancePolicyProduct.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.insurancePolicy.onUpdateInsurancePolicyProduct] error',
    })

    yield put(AdminUpdateInsurancePolicyProduct.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to update this policy product'))
    }
  }
}

export function* onDeleteInsurancePolicyProduct(
  action: AsyncAction<DeleteInsurancePolicyProductPayload>,
) {
  const insurancePolicyPermanentId = action.payload?.insurancePolicyPermanentId
  const insuranceProductId = action.payload?.insuranceProductId

  if (!insurancePolicyPermanentId) {
    yield put(
      AdminDeleteInsurancePolicyProduct.failed(new Error('Missing insurancePolicyPermanentId')),
    )

    if (action.next) {
      action.next('Missing insurancePolicyPermanentId')
    }

    return
  }

  if (!insuranceProductId) {
    yield put(AdminDeleteInsurancePolicyProduct.failed(new Error('Missing insuranceProductId')))

    if (action.next) {
      action.next('Missing insuranceProductId')
    }

    return
  }

  try {
    yield call(callSecureApi, Endpoints.AdminInsurancePolicyProduct, {
      method: 'DELETE',
      params: [
        { field: ':insurancePolicyPermanentId', value: insurancePolicyPermanentId },
        { field: ':insuranceProductId', value: insuranceProductId },
      ],
      // Always immutable, matching Retool: removing a product must not silently invalidate the
      // documents contractors have already submitted for it. Not a form choice.
      query: { operationType: InsurancePolicyOperationType.Immutable },
    })

    yield put(AdminDeleteInsurancePolicyProduct.success({ insuranceProductId }))

    if (action.next) {
      action.next(null, insuranceProductId)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.insurancePolicy.onDeleteInsurancePolicyProduct] error',
    })

    yield put(AdminDeleteInsurancePolicyProduct.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to remove this product from the policy'))
    }
  }
}

export function* insurancePolicySaga() {
  // `takeLatest`, unlike the `takeEvery` this handler had in `collectStepReference`: the
  // Insurance tab refetches after every mutation, so overlapping runs are routine and a stale
  // response must never overwrite a newer one.
  yield takeLatest(AdminFetchInsurancePolicies.type.REQUEST, onFetchInsurancePolicies)
  // Mutations must each run.
  yield takeEvery(AdminCreateInsurancePolicy.type.REQUEST, onCreateInsurancePolicy)
  yield takeEvery(AdminUpdateInsurancePolicy.type.REQUEST, onUpdateInsurancePolicy)
  yield takeEvery(AdminDeleteInsurancePolicy.type.REQUEST, onDeleteInsurancePolicy)
  yield takeEvery(AdminCreateInsurancePolicyProduct.type.REQUEST, onCreateInsurancePolicyProduct)
  yield takeEvery(AdminUpdateInsurancePolicyProduct.type.REQUEST, onUpdateInsurancePolicyProduct)
  yield takeEvery(AdminDeleteInsurancePolicyProduct.type.REQUEST, onDeleteInsurancePolicyProduct)
}
